import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function DashboardLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Email and password are required");
      return;
    }
    try {
      setLoading(true);
      await login(email, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Unable to sign in");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-stone-50">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-ink text-white">
        <Link to="/" className="font-display text-xl tracking-tight" data-testid="login-logo">
          CA&nbsp;Nitin&nbsp;Sharma<span className="text-terracotta">.</span>
        </Link>
        <div>
          <p className="overline text-white/60"><span className="section-mark">Admin</span></p>
          <h1 className="font-display mt-6 text-5xl xl:text-6xl font-light tracking-[-0.03em] leading-[1.02]">
            Lead<br /><span className="font-display-italic">Dashboard</span>.
          </h1>
          <p className="mt-6 text-white/70 max-w-md text-[15px] leading-relaxed">
            Where every Discovery Call and Brand Collaboration lead lives. Triage, follow up,
            and keep the pipeline moving.
          </p>
        </div>
        <p className="text-xs text-white/50">© {new Date().getFullYear()} CA Nitin Sharma</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form
          onSubmit={submit}
          data-testid="login-form"
          className="w-full max-w-sm bg-white border border-stone-200 rounded-2xl p-8 sm:p-10"
        >
          <p className="overline"><span className="section-mark">Sign in</span></p>
          <h2 className="font-display mt-5 text-3xl font-light tracking-tight text-ink leading-tight">
            Welcome <span className="font-display-italic">back</span>.
          </h2>
          <p className="mt-2 text-sm text-ink-soft">Use your admin credentials.</p>

          <label className="block mt-8">
            <span className="overline">Email</span>
            <input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="input-airy mt-2 w-full bg-transparent border-0 border-b border-stone-300 py-3 text-lg focus:border-terracotta"
              autoComplete="email"
            />
          </label>

          <label className="block mt-6">
            <span className="overline">Password</span>
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="input-airy mt-2 w-full bg-transparent border-0 border-b border-stone-300 py-3 text-lg focus:border-terracotta"
              autoComplete="current-password"
            />
          </label>

          <button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="mt-10 w-full inline-flex items-center justify-center rounded-full bg-ink text-white px-7 py-3.5 text-sm font-medium tracking-wide hover:bg-terracotta transition-colors disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>

          <Link
            to="/"
            className="mt-6 block text-center text-xs text-ink-soft hover:text-ink"
            data-testid="login-back"
          >
            ← Back to site
          </Link>
        </form>
      </div>
    </div>
  );
}
